import React, { useState } from 'react';
import { Send, X, Plus, Trash2 } from 'lucide-react';
import Button from './ui/button';
import Input from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const SendWebhookModal = ({ isOpen, onClose, onSendWebhook, runData }) => {
  const [formData, setFormData] = useState({
    webhook_url: '',
    format: 'summary',
    include_raw_data: false,
    headers: [{ key: '', value: '' }],
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.webhook_url.trim()) {
        alert('Please enter a webhook URL');
        return;
      }

      // Filter out empty headers
      const validHeaders = {};
      formData.headers.forEach(header => {
        if (header.key.trim() && header.value.trim()) {
          validHeaders[header.key.trim()] = header.value.trim();
        }
      });

      await onSendWebhook({
        webhook_url: formData.webhook_url.trim(),
        format: formData.format,
        include_raw_data: formData.include_raw_data,
        headers: validHeaders,
      });
      onClose();
    } catch (error) {
      console.error('Failed to send webhook:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addHeader = () => {
    setFormData(prev => ({
      ...prev,
      headers: [...prev.headers, { key: '', value: '' }]
    }));
  };

  const removeHeader = (index) => {
    setFormData(prev => ({
      ...prev,
      headers: prev.headers.filter((_, i) => i !== index)
    }));
  };

  const updateHeader = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      headers: prev.headers.map((header, i) =>
        i === index ? { ...header, [field]: value } : header
      )
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-md w-full max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <Send className="w-5 h-5" />
              <span>Send Results via Webhook</span>
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Run Info */}
            <div className="p-3 bg-secondary/50 rounded-lg">
              <p className="text-sm text-muted-foreground">Evaluation Run</p>
              <p className="font-medium">{runData?.evaluation_name}</p>
              <p className="text-sm text-muted-foreground">
                Run ID: {runData?.run_id} | Status: {runData?.status}
              </p>
              {runData?.overall_score && (
                <p className="text-sm text-muted-foreground">
                  Score: {Math.round(runData.overall_score * 100)}%
                </p>
              )}
            </div>

            {/* Webhook URL */}
            <div>
              <label className="block text-sm font-medium mb-2">Webhook URL</label>
              <Input
                type="url"
                placeholder="https://your-webhook-endpoint.com/webhook"
                value={formData.webhook_url}
                onChange={(e) => handleChange('webhook_url', e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                The endpoint where results will be sent via HTTP POST
              </p>
            </div>

            {/* Format */}
            <div>
              <label className="block text-sm font-medium mb-2">Format</label>
              <select
                value={formData.format}
                onChange={(e) => handleChange('format', e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
              >
                <option value="summary">Summary</option>
                <option value="detailed">Detailed</option>
                <option value="json">JSON</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                {formData.format === 'summary' && 'Basic results overview'}
                {formData.format === 'detailed' && 'Includes individual test case results'}
                {formData.format === 'json' && 'Complete structured data'}
              </p>
            </div>

            {/* Include Raw Data */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="include_raw_data_webhook"
                checked={formData.include_raw_data}
                onChange={(e) => handleChange('include_raw_data', e.target.checked)}
                className="rounded border-input"
              />
              <label htmlFor="include_raw_data_webhook" className="text-sm">
                Include raw data (for debugging)
              </label>
            </div>

            {/* Custom Headers */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium">Custom Headers</label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addHeader}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
              <div className="space-y-2">
                {formData.headers.map((header, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Input
                      placeholder="Header name"
                      value={header.key}
                      onChange={(e) => updateHeader(index, 'key', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      placeholder="Header value"
                      value={header.value}
                      onChange={(e) => updateHeader(index, 'value', e.target.value)}
                      className="flex-1"
                    />
                    {formData.headers.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeHeader(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Optional custom headers to include with the webhook request
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Sending...' : 'Send Webhook'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SendWebhookModal;
